import { differenceInMinutes, differenceInSeconds } from "date-fns";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useIsModerator } from "@/features/participants/api/is-moderator";
import { useGetSessionByKey } from "@/features/session/api/get-session-by-key";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";
import { cn } from "@/lib/utils";
import { isSessionConflict } from "@/types/error";
import { BreakoutSessionClosedReason } from "@classview/shared-types";
import NumberFlow, { NumberFlowGroup } from "@number-flow/react";

import { useExtendSession } from "../api/extend-session";

type SessionCountdownProps = {
  className?: string;
};

type SessionState = "future" | "active" | "ended";

export function SessionCountdown({ className }: SessionCountdownProps) {
  const { key } = useParams();
  const [timeRemaining, setTimeRemaining] = useState("00:00");
  const [isWithin5Minutes, setIsWithin5Minutes] = useState(false);
  const [sessionState, setSessionState] = useState<SessionState>("active");

  const { data: session, isSuccess: isSessionSuccess } = useGetSessionByKey(
    key ?? "",
  );

  const setConflictingSessions = useVidyoStore(
    (state) => state.moderationDrawer.setConflictingSessions,
  );

  const openModerationDrawer = useVidyoStore(
    (state) => state.moderationDrawer.open,
  );

  const closeBreakoutSession = useVidyoStore(
    (state) => state.breakoutSession.closeBreakoutSession,
  );

  const { mutate: extendSession, isPending: isExtendingSession } =
    useExtendSession({
      onMutate: () => {
        setConflictingSessions(undefined);
      },
      onSuccess: () => {
        toast.success("Session extended");
      },
      onError: (error) => {
        if (isSessionConflict(error)) {
          setConflictingSessions(error.conflictingSessions);
        } else {
          toast.error(error.message);
        }

        openModerationDrawer("extend-session");
      },
    });
  const isModerator = useIsModerator();

  useEffect(() => {
    if (!isSessionSuccess) return;

    const checkSessionState = () => {
      const now = new Date();
      const startTime = new Date(session.start);
      const endTime = new Date(session.end);

      if (now < startTime) {
        setSessionState("future");
        setIsWithin5Minutes(false);
      } else if (now >= endTime) {
        setSessionState("ended");
        setIsWithin5Minutes(false);
      } else {
        setSessionState("active");
        const diffInMinutes = differenceInMinutes(endTime, now);
        setIsWithin5Minutes(diffInMinutes <= 5);
      }
    };

    // Check immediately
    checkSessionState();

    // Calculate time until next state change
    const calculateNextCheck = () => {
      const now = new Date();
      const startTime = new Date(session.start);
      const endTime = new Date(session.end);

      if (now < startTime) {
        // If future, check every second until start time
        return 1000;
      } else if (now >= endTime) {
        // If ended, check every minute
        return 60000;
      } else {
        // If active, check every minute
        return 60000;
      }
    };

    // Initial interval
    let interval = setInterval(() => {
      checkSessionState();
      // Update interval based on current state
      clearInterval(interval);
      interval = setInterval(checkSessionState, calculateNextCheck());
    }, calculateNextCheck());

    return () => clearInterval(interval);
  }, [isSessionSuccess, session]);

  useEffect(() => {
    if (!isSessionSuccess) return;

    const updateTimeRemaining = () => {
      const now = new Date();
      const startTime = new Date(session.start);
      const endTime = new Date(session.end);

      let diffInSeconds: number;

      if (sessionState === "future") {
        diffInSeconds = differenceInSeconds(startTime, now);
      } else if (sessionState === "active") {
        diffInSeconds = differenceInSeconds(endTime, now);
      } else {
        diffInSeconds = 0;
      }

      if (diffInSeconds <= 0) {
        clearInterval(interval);
        setTimeRemaining("00:00");
        return;
      }

      const minutes = Math.floor(diffInSeconds / 60);
      const seconds = diffInSeconds % 60;
      setTimeRemaining(
        `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`,
      );
    };

    // Update countdown every second
    const interval = setInterval(updateTimeRemaining, 1000);
    updateTimeRemaining();
    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, [session, isSessionSuccess, sessionState]);

  const isEnded = sessionState === "ended";

  useEffect(() => {
    if (isEnded && isSessionSuccess && session.parentSession) {
      closeBreakoutSession({
        sessionId: session.id,
        reason: BreakoutSessionClosedReason.BREAKOUT_SESSION_TIMEOUT,
      });
    }
  }, [
    isEnded,
    session?.id,
    session?.parentSession,
    closeBreakoutSession,
    isSessionSuccess,
  ]);

  if (!isSessionSuccess) {
    return null;
  }

  if (isEnded) {
    return (
      <div className={cn("px-2", className)}>
        <span className="text-sm text-muted">This session has ended!</span>
      </div>
    );
  }

  if (sessionState === "future") {
    return (
      <div className={cn("px-2", className)}>
        <span className="text-sm text-muted">Session starts in: </span>
        <NumberFlowGroup>
          <div
            style={{
              fontVariantNumeric: "tabular-nums",
            }}
            className="mr-3 inline-flex items-baseline text-sm text-white"
          >
            <NumberFlow
              trend={-1}
              value={Number(timeRemaining.split(":")[0])}
              digits={{ 1: { max: 5 } }}
              format={{ minimumIntegerDigits: 2 }}
            />
            <NumberFlow
              prefix=":"
              trend={-1}
              value={Number(timeRemaining.split(":")[1])}
              digits={{ 1: { max: 5 } }}
              format={{ minimumIntegerDigits: 2 }}
            />
          </div>
        </NumberFlowGroup>
      </div>
    );
  }

  const [mm, ss] = timeRemaining.split(":");

  return (
    <div className={cn("px-2", className)}>
      <NumberFlowGroup>
        <div
          style={{
            fontVariantNumeric: "tabular-nums",
          }}
          className="mr-3 inline-flex items-baseline text-sm text-white"
        >
          <NumberFlow
            trend={-1}
            value={Number(mm)}
            digits={{ 2: { max: 5 } }}
            format={{ minimumIntegerDigits: 2 }}
          />
          <NumberFlow
            prefix=":"
            trend={-1}
            value={Number(ss)}
            digits={{ 2: { max: 5 } }}
            format={{ minimumIntegerDigits: 2 }}
          />
        </div>
      </NumberFlowGroup>

      {isModerator && isWithin5Minutes && (
        <Button
          variant="ghost"
          className="px-1"
          size="xs"
          disabled={isExtendingSession}
          isLoading={isExtendingSession}
          isAnimated
          onClick={() =>
            extendSession({
              sessionId: session.id,
              payload: { extensionInMinutes: 5 },
            })
          }
        >
          Extend 5 Minutes
        </Button>
      )}
    </div>
  );
}
